import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, Pencil, Trash2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { ddmmyyyy, inr, num, LR_STATUSES, lrSlug } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { ConfirmDialog } from "../setup/ConfirmDialog";
import { diffClass, diffLabel } from "./GRNItemCard";

export const LRChip = ({ status, testid }) => <span className={`chip chip-lr chip-lr-${lrSlug(status)}`} data-testid={testid}>{status}</span>;

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-3 py-1 text-sm">
    <span className="text-muted-foreground">{k}</span>
    <span className="min-w-0 break-words text-right font-medium">{v || "—"}</span>
  </div>
);

const GRNDetail = ({ grn, onChanged, t }) => {
  const navigate = useNavigate();
  const { isAdmin, can } = useAuth();
  const [confirm, setConfirm] = useState(false);

  const setLr = async (e) => {
    const lr_status = e.target.value;
    try {
      await api.patch(`/grns/${grn.id}/lr-status`, { lr_status });
      toast.success(`${grn.grn_no}: ${lr_status}`);
      onChanged();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const remove = async () => {
    setConfirm(false);
    try {
      await api.delete(`/grns/${grn.id}`);
      toast.success("GRN deleted");
      onChanged();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="rise space-y-4 border-t bg-muted/20 p-4" data-testid={`${t}-detail`}>
      <div>
        <Row k="Receiving location" v={grn.receiving_location} />
        {grn.fabricator_name && <Row k="Factory" v={grn.fabricator_name} />}
        <Row k="Vendor invoice" v={[grn.invoice_no, ddmmyyyy(grn.invoice_date)].filter(Boolean).join(" · ")} />
        <Row k="LR" v={[grn.lr_no, ddmmyyyy(grn.lr_date)].filter(Boolean).join(" · ")} />
        <Row k="Transporter" v={grn.transporter} />
      </div>
      <div>
        <h3 className="mono-label mb-1 text-muted-foreground">Items</h3>
        {grn.items.filter((it) => it.qty_invoice > 0 || it.qty_received > 0).map((it, i) => (
          <div key={it.po_item_id} className="space-y-1 border-b py-3 last:border-0" data-testid={`${t}-item-${i}`}>
            <div className="flex justify-between gap-3">
              <p className="min-w-0 font-semibold">{it.description}{it.colour ? ` (${it.colour})` : ""} {it.code && <span className="mono text-xs text-muted-foreground">[{it.code}]</span>}</p>
              <p className="mono shrink-0 font-bold">{inr(it.total)}</p>
            </div>
            <p className="mono text-xs text-muted-foreground">
              Balance {num(it.qty_balance)} · Invoice {num(it.qty_invoice)} · Received {num(it.qty_received)} {it.uom}
            </p>
            <p className={`mono text-xs font-bold ${diffClass(it.difference)}`}>{diffLabel(it.difference)} · value {inr(it.value)} + tax {inr(it.tax)}</p>
          </div>
        ))}
      </div>
      <div className="rounded-lg bg-card p-3">
        <Row k="Value received" v={inr(grn.value)} />
        <Row k="Tax" v={inr(grn.tax)} />
        <div className="flex justify-between border-t pt-2 font-bold"><span>Total</span><span className="mono">{inr(grn.total)}</span></div>
      </div>
      {grn.remarks && <p className="text-sm"><span className="text-muted-foreground">Remarks: </span>{grn.remarks}</p>}
      {grn.items.some((it) => it.flags?.length) && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs" data-testid={`${t}-mismatch`}>
          <p className="font-bold text-destructive">3-way match: PO vs invoice vs received</p>
          {grn.items.filter((it) => it.flags?.length).map((it) => <p key={it.po_item_id}>{it.description}: {it.flags.join("; ")}</p>)}
        </div>
      )}
      <Returns grn={grn} t={t} canWrite={can("grn_write")} isAdmin={isAdmin} onChanged={onChanged} />
      {can("grn_write") && <div className="grid grid-cols-2 gap-2">
        <select className="field font-semibold" value={grn.lr_status} onChange={setLr} data-testid={`${t}-lr-status-select`}>
          {LR_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <button className="btn btn-outline" onClick={() => navigate(`/grns/${grn.id}/edit`)} data-testid={`${t}-edit-btn`}><Pencil size={18} /> Edit</button>
      </div>}
      {isAdmin && (
        <button className="btn btn-outline btn-ghost-danger w-full" onClick={() => setConfirm(true)} data-testid={`${t}-delete-btn`}><Trash2 size={18} /> Delete GRN</button>
      )}
      <ConfirmDialog open={confirm} onCancel={() => setConfirm(false)} onConfirm={remove} title={`Delete ${grn.grn_no}?`}
        body="Received quantities on the PO will be recalculated." />
    </div>
  );
};

const Returns = ({ grn, t, canWrite, isAdmin, onChanged }) => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const load = () => api.get("/purchase-returns", { params: { grn_id: grn.id } }).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, [grn.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (r) => {
    try {
      await api.delete(`/purchase-returns/${r.id}`);
      toast.success(`${r.pr_no} deleted`);
      load();
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-2" data-testid={`${t}-returns`}>
      {list.map((r) => (
        <div key={r.id} className="flex items-center justify-between gap-2 rounded-lg border p-2 text-xs" data-testid={`${t}-return-${r.pr_no}`}>
          <span><b className="mono">{r.pr_no}</b> {ddmmyyyy(r.date)}{r.debit_note_no ? ` · DN ${r.debit_note_no}` : ""} · {r.items.map((i) => `${i.description} ${i.qty} ${i.uom}`).join(", ")} · {inr(r.total)}</span>
          {isAdmin && <button className="icon-btn hover:!text-destructive" onClick={() => del(r)} aria-label="Delete return" data-testid={`${t}-return-${r.pr_no}-delete`}><Trash2 size={16} /></button>}
        </div>
      ))}
      {canWrite && <button className="btn btn-outline w-full" onClick={() => navigate(`/grns/${grn.id}/return`)} data-testid={`${t}-return-btn`}>Purchase return</button>}
    </div>
  );
};

export const GRNCard = ({ grn, open, onToggle, onChanged }) => {
  const t = `grn-card-${grn.grn_no}`;
  return (
    <div className="panel overflow-hidden" data-testid={t}>
      <button type="button" onClick={onToggle} className="flex w-full items-start gap-3 p-4 text-left transition-colors hover:bg-muted/40" data-testid={`${t}-toggle`}>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mono font-bold">{grn.grn_no}</span>
            <LRChip status={grn.lr_status} testid={`${t}-lr-status`} />
            {grn.mismatch && <span className="chip border-destructive/50 text-destructive" data-testid={`${t}-mismatch-chip`}>Mismatch</span>}
          </div>
          <p className="truncate font-semibold">{grn.vendor_name}</p>
          <p className="text-xs text-muted-foreground">{grn.po_no} · {ddmmyyyy(grn.grn_date)}{grn.lr_no ? ` · LR ${grn.lr_no}` : ""}</p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="mono font-bold">{inr(grn.total)}</span>
          <ChevronDown size={18} className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && <GRNDetail grn={grn} onChanged={onChanged} t={t} />}
    </div>
  );
};
